import { createClient, type SanityClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';
import type { ImageUrlBuilder } from '@sanity/image-url/lib/types/builder';
import type { SanityImageSource } from '@sanity/image-url/lib/types/types';
import type { AppConfig } from 'modules/config/appConfig';

const sanityClientDeferred = (() => {
  const deferred = {} as { promise: Promise<SanityClient>; resolve: (client: SanityClient) => void };
  deferred.promise = new Promise<SanityClient>((resolve) => {
    deferred.resolve = resolve;
  });
  return deferred;
})();

export class CmsService {
  clientPromise = sanityClientDeferred.promise;
  builder: ImageUrlBuilder | undefined;

  init = (config: AppConfig) => {
    const client = createClient(config.cmsConfig);
    sanityClientDeferred.resolve(client);
    this.builder = imageUrlBuilder(client);
  };

  getDocumentsByQuery = (query: string) => this.clientPromise.then((client) => client.fetch(query));

  getDocumentsByType = (type: 'channel' | 'product') => {
    const query = `*[_type == '${type}']`;
    return this.clientPromise.then((client) => client.fetch(query));
  };

  getImage = (data: SanityImageSource) => {
    try {
      return this.builder?.image(data);
    } catch {
      return null;
    }
  };
  getImageUrl = (data?: SanityImageSource) => {
    if (data === undefined) {
      return '';
    }
    const image = this.getImage(data);
    try {
      return image ? image.url() : '';
    } catch {
      return '';
    }
  };
  /**
   * Returns url that crops image from center to provided size.
   * Format will always be set to webp since svg's cant be cropped in sanity image pipeline
   */
  getSquareImage(data: SanityImageSource, sizeInPx: number) {
    /* Need to set format as "png" since svg's wont be cropped */
    return (
      this.getImage(data)
        ?.fit('fill')
        .crop('center')
        .bg('0001')
        .format(supportsWebPFormat ? 'webp' : 'png')
        .size(sizeInPx, sizeInPx)
        .url() ?? ''
    );
  }
}

const supportsWebPFormat = (() => {
  try {
    const elem = document.createElement('canvas');
    if (elem.getContext?.('2d')) {
      // was able or not to get WebP representation
      return elem.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    }
    // very old browser like IE 8, canvas not supported
    return false;
  } catch {
    return false;
  }
})();
